import { Link, useParams } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import NewsCard from '@/components/feature/NewsCard';
import ShareButton from '@/components/feature/ShareButton';
import Seo from '@/components/feature/Seo';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((item) => item.id === id) ?? null;

  if (!article) {
    return (
      <div className="px-4 md:px-6 lg:px-10 py-24 flex flex-col items-center text-center">
        <Seo title="资讯不存在 - 上司的老婆" description="未找到对应的影视资讯内容。" />
        <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
          <i className="ri-newspaper-line"></i>
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-900">没有找到这条资讯</h1>
        <p className="text-sm text-foreground-500 mt-2">它可能已被移除，或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = newsList.filter((item) => item.id !== article.id && item.category === article.category).slice(0, 3);
  const fallback = newsList.filter((item) => item.id !== article.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallback;

  return (
    <div className="w-full">
      <Seo
        title={`${article.title} - 影视资讯 - 上司的老婆`}
        description={article.summary}
        keywords={`${article.category},影视资讯,${article.title}`}
      />

      <div className="px-4 md:px-6 lg:px-10 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/news" className="hover:text-primary-600 transition-colors cursor-pointer">影视资讯</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700 line-clamp-1">{article.category}</span>
        </nav>
      </div>

      <section className="px-4 md:px-6 lg:px-10 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
          <article>
            <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs mb-4">
              <i className="ri-price-tag-3-line"></i>
              {article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 leading-snug">{article.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                上司的老婆编辑部
              </span>
            </div>

            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mt-6">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title} - 影视资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              <p className="text-base md:text-lg text-foreground-700 leading-loose font-medium">{article.summary}</p>
              {article.content.map((paragraph) => (
                <p key={paragraph.slice(0, 12)} className="text-sm md:text-base text-foreground-700 leading-loose">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-background-200">
              <span className="text-xs text-foreground-400">本文为演示内容，仅用于页面展示</span>
              <ShareButton title={`${article.title} - 上司的老婆影视资讯`} />
            </div>
          </article>

          <aside>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-primary-500"></i>
                相关资讯
              </h2>
              <div className="flex flex-col gap-3">
                {relatedList.map((item) => (
                  <NewsCard key={item.id} article={item} layout="horizontal" />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}